import React, { useMemo, useEffect, useState } from 'react'
import { Box, Pagination } from '@maui/elements'
import {
  FilterSortTable,
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useFilterTypes,
  useDefaultColumn,
} from '../Table'
import { indicatorTableColumns, indicatorTableColumnsWithThreatScore } from './TableHeader'
import { CustomCell } from './TableCell'
import { useUpdateBodyParams } from '@/hooks/useUpdateBodyParams'
import { PageLoading } from '../PageLoading'
import { IndicatorsTableContextProvider } from './IndicatorsTableContext'

export const IndicatorsTable = ({
  data,
  includeThreatRating,
  isGTI,
  isLoading,
  navigate,
  searchFilter,
  setIndicatorParams,
  virusTotalRoot,
  type,
}) => {
  const [filteredIndicatorsData, setFilteredIndicatorsData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const filterTypes = useFilterTypes()
  const defaultColumn = useDefaultColumn()

  const pageOptions = [25, 50, 100]
  const defaultPageSize = pageOptions[2]
  const columns = includeThreatRating ? indicatorTableColumnsWithThreatScore : indicatorTableColumns

  const {
    headers,
    prepareRow,
    getTableProps,
    getTableBodyProps,
    page,
    setPageSize,
    state: { pageSize, filters, sortBy }
  } = useTable({
    columns,
    data: filteredIndicatorsData?.indicators || [],
    defaultColumn,
    defaultCanSort: true,
    manualFilters: true,
    manualSortBy: true,
    manualPagination: true,
    initialState: {
      pageIndex: 0,
      pageSize: defaultPageSize,
      ...(searchFilter && {
        filters: [
          {
            id: 'type',
            value: searchFilter
          }
        ]
      })
    },
    filterTypes,
    autoResetSortBy: true
  },
  useFilters,
  useSortBy,
  usePagination
  )

  useEffect(() => {
    if (data) setFilteredIndicatorsData(data)
  }, [data])

  const specificFilters = useMemo(() => {
    return filters.reduce((acc, { id, value }) => {
      if (id === 'type') {
        acc.indicatorTypeFilter = value
      } else if (id === 'value') {
        acc.indicatorValue = value
      } else if (id.includes('Associations')) {
        acc.associationFilters.push(value)
      }
      return acc
    }, { associationFilters: [], indicatorTypeFilter: null, indicatorValue: null })
  }, [filters])

  const bodyParams = useUpdateBodyParams({ specificFilters, includeThreatRating, currentPage, pageSize, sortBy })

  useEffect(() => {
    setIndicatorParams && setIndicatorParams(bodyParams)
  }, [bodyParams, setIndicatorParams])

  const hasFilters = !!filters.length
  const hasIndicators = !!data?.indicators?.length

  // Initially with no filters
  if (isLoading && !hasFilters) return <PageLoading />

  // No data and no filters tell the user
  if (!hasIndicators && !hasFilters) {
    return <Box>No indicators found for this {type}</Box>
  }

  // Render the table when:
  // - Has indicators
  // - No indicators returned after filtering
  return (
    <IndicatorsTableContextProvider
      isGTI={isGTI}
      navigate={navigate}
      virusTotalRoot={virusTotalRoot}
    >
      <FilterSortTable
        headers={headers}
        prepareRow={prepareRow}
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        page={page}
        customCell={CustomCell}
      />
      {hasIndicators
        ? <Pagination
          mt="spaceGapLarge"
          count={filteredIndicatorsData?.count?.total || 0}
          perPage={pageSize}
          perPageOptions={pageOptions}
          setPerPage={setPageSize}
          page={currentPage}
          setPage={(page) => setCurrentPage(page)}
          boundaryCount={1}
          siblingCount={1}
        />
        : <Box variant="body2" p="spacePaddingLarge">No indicators found for {type} matching selected filter(s)</Box>
      }
    </IndicatorsTableContextProvider>
  )
}

export default IndicatorsTable
